import type { Location } from "./types/location";
import type { TerminalUpdate } from "./types/terminalUpdates";
import type {
  BerthRotation,
  HusbandrySpareBerthSummary,
} from "./types/voyages";
import type { AuditError, Schedule } from "./types/voyageSchedule";

interface Calls {
  berth_id: string;
  type: string;
  operations?: any[] | null;
  deliveries?: any[] | null;
  on_board_services?: any[] | null;
  bunkers?: any[] | null;
}

interface Job {
  job_id: string;
  vessel: string;
  status: string;
  assigned_agent: string | null;
  agent: {
    phone: string;
    email: string;
  } | null;
  voyage: string;
  berth_rotations: BerthRotation[];
  berths: {
    calls: Calls[];
  }[];
  husbandry_spare_summaries?: HusbandrySpareBerthSummary[];
}

export const getBerthData = (job: Job) => {
  try {
    const berthRotations: BerthRotation[] = job.berth_rotations;
    const berthList = job.berths.reduce(
      (acc: Calls[], berths) => [...acc, ...berths.calls],
      []
    );
    
    // Helper to get call data (operations, deliveries, etc.) by berth_id
    const getCallData = (berthId: string) => {
      const call = berthList.find((c) => c.berth_id === berthId);
      return {
        operations: call?.operations || null,
        deliveries: call?.deliveries || null,
        on_board_services: call?.on_board_services || null,
        bunkers: call?.bunkers || null,
        sparesSummary:
          job.husbandry_spare_summaries?.find(
            (summary) => summary.berth_id === berthId
          ) ?? null,
      };
    };
    const currentTime = new Date();

    // Find current berth without mutation
    const currentBerthInfo =
      berthRotations
        .map((berth, index) => {
          const eta = new Date(berth.eta);
          const etd = new Date(berth.etd);
          const shiftingStart = etd;
          const shiftingEnd = berth.shifting_hours
            ? new Date(etd.getTime() + berth.shifting_hours * 60 * 60 * 1000)
            : null;

          if (
            shiftingEnd &&
            currentTime >= shiftingStart &&
            currentTime < shiftingEnd
          ) {
            return {
              ...berth,
              status: "Shifting",
              index,
              type: null,
              isCurrentBerth: true,
            };
          }

          if (
            currentTime >= eta &&
            (currentTime < shiftingStart || !shiftingEnd)
          ) {
            return {
              ...berth,
              status: "At Berth",
              index,
              type:
                berthList.find((call) => call.berth_id === berth.berth_id)
                  ?.type || null,
              isCurrentBerth: true,
            };
          }

          return { ...berth, isCurrentBerth: false };
        })
        .find((b) => b.isCurrentBerth) || null;

    // Process current and next berth
    const currentBerth = currentBerthInfo
      ? (({ isCurrentBerth: _isCurrentBerth, ...rest }) => ({
          ...rest,
          ...getCallData(rest.berth_id),
        }))(currentBerthInfo)
      : null;

    let nextBerth: BerthRotation | null = null;
    if (currentBerth) {
      const nextIndex = currentBerth.index + 1;
      nextBerth = berthRotations[nextIndex]
        ? {
            ...berthRotations[nextIndex],
            ...getCallData(berthRotations[nextIndex].berth_id),
            type:
              berthList.find(
                (call) => call.berth_id === berthRotations[nextIndex].berth_id
              )?.type || null,
          }
        : null;
    } else {
      nextBerth = berthRotations[0]
        ? {
            ...berthRotations[0],
            ...getCallData(berthRotations[0].berth_id),
            type:
              berthList.find(
                (call) => call.berth_id === berthRotations[0].berth_id
              )?.type || null,
          }
        : null;
    }

    return {
      currentBerth,
      nextBerth,
    };
  } catch {
    return null;
  }
};

export const getCurrentBerthStatus = (berth: Schedule) => {
  try {
    const currentTime = new Date();
    const eta = new Date(berth.eta);
    const etd = new Date(berth.etd);
    const shiftingStart = etd;
    const shiftingEnd = berth.shifting
      ? new Date(etd.getTime() + berth.shifting * 60 * 60 * 1000)
      : null;
    if (
      shiftingEnd &&
      currentTime >= shiftingStart &&
      currentTime < shiftingEnd
    ) {
      return {
        status: "Shifting",
      };
    }
    if (currentTime >= eta && (currentTime < shiftingStart || !shiftingEnd)) {
      return {
        status: "At Berth",
      };
    }
    return {
      status: "Pending",
    };
  } catch {
    return {
      status: "Pending",
    };
  }
};

export const getCallsByBerthId = (voyageList: any, berthId: string) => {
  try {
    const voyage = voyageList.find((v: any) =>
      v.portsDetails.some((p: any) => p.berthId === berthId)
    );

    if (!voyage) return [];
    const port = voyage.portsDetails.find(
      (p: { berthId: string }) => p.berthId === berthId
    );
    return port?.calls || [];
  } catch {
    return [];
  }
};

export function auditBerths(berths: Schedule[]): Schedule[] {
  try {
    const warnings: string[] = [];
    const auditFunctions: {
      [key: string]: (pBerth: Schedule, cBerth: Schedule) => AuditError | false;
    } = {
      checkLocation: (
        pBerth: Schedule,
        cBerth: Schedule
      ): AuditError | false => {
        if (!cBerth.berth_location?.location_id) {
          return {
            errorScore: 999,
            errors:
              'Berth location cannot be empty. Select a berth the the "Stop" picklist.',
          };
        }
        if (
          pBerth.berth_location?.location_id ===
          cBerth.berth_location?.location_id
        ) {
          return {
            errors:
              "The previous location and current location are the same. Please check the values berths since this is not allowed.",
          };
        }
        return false;
      },
      checkShiftingHoursExist: (
        pBerth: Schedule,
        _cBerth: Schedule
      ): AuditError | false => {
        if (!pBerth.berth_location) return false;
        if (!pBerth.shifting || pBerth.shifting === 99) {
          return {
            errors: `Shifting hours between ${pBerth.berth_location?.name} and current location is not added due to possible missing entry in shifting matrix. 99 hours has been taken as default value until corrected.`,
          };
        }
        return false;
      },

      // Add more audit functions as needed
    };

    berths.forEach((berth, i) => {
      const errors: AuditError[] = [];
      if (i !== 0) {
        const currentLocation = berth;
        const prevLocation = berths[i - 1];
        Object.keys(auditFunctions).forEach((functionKey) => {
          const auditErrors = auditFunctions[functionKey](
            prevLocation,
            currentLocation
          );
          if (auditErrors) {
            errors.push(auditErrors);
          }
        });
        if (errors.length > 0) {
          currentLocation.isError = true;
          currentLocation.error_info = errors;
          currentLocation.warnings = warnings;
        }
      }
      if (i === 0 || i === berths.length - 1) {
        const berthLoc = berth.berth_location;
        if (!berthLoc) {
          errors.push({
            errorScore: 999,
            errors:
              'Berth location cannot be empty. Select a berth the the "Stop" picklist.',
          });
        } else if (berthLoc.type?.value !== "Anchorage") {
          errors.push({
            errors: `${i === 0 ? "First" : "Last"} Berth Should be Anchorage.`,
          });
        }
        if (errors.length > 0) {
          berth.isError = true;
          berth.error_info = errors;
        }
      }
    });

    return berths;
  } catch {
    return berths;
  }
}

/**
 * Returns active TerminalUpdates and Location notes for a given berth (locationId).
 * Matches terminal updates by:
 *   1. Direct locationId match
 *   2. Terminal company match (companyId on the Location record at runtime)
 *   3. berthUpdates sub-entry match
 * Used in Voyages column cells and the Full View notes section.
 */
export function getNotesForBerth(
  berthId: string,
  terminalUpdates: TerminalUpdate[],
  locations: Location[],
  ownerCompanyId: string | null = null
) {
  const now = new Date();
  const location = locations.find((l) => l.locationId === berthId);
  const locationCompanyId: string | null = location
    ? ((location as Record<string, any>).companyId ?? null)
    : null;
  const effectiveCompanyId = ownerCompanyId ?? locationCompanyId;

  const activeUpdates = terminalUpdates.filter((u) => {
    if (u.effectiveEnd && new Date(u.effectiveEnd) <= now) return false;
    return (
      u.locationId === berthId ||
      (effectiveCompanyId != null && u.companyId === effectiveCompanyId) ||
      u.berthUpdates?.some((b) => b.locationId === berthId)
    );
  });

  const locationNotes = {
    notes: location?.notes ?? null,
    publicNotes: location?.publicNotes ?? null,
    internalNotes: location?.internalNotes ?? null,
    prospectNotes: location?.prospectNotes ?? null,
  };
  const hasAnyNotes =
    activeUpdates.length > 0 || Object.values(locationNotes).some(Boolean);
  return { activeUpdates, locationNotes, location, hasAnyNotes };
}

/**
 * Resolves the prearrival-form requirement for a berth rotation entry.
 * `locationId` must be the berth's `berth_location_id` (not `berth_id`).
 * Visibility depends solely on `requiresPrearrival`; notes are only the tooltip text.
 */
export function getPrearrivalForLocation(
  locationId: string | null | undefined,
  locations: Location[]
): { required: boolean; notes: string } {
  if (!locationId) return { required: false, notes: "" };
  const location = locations.find((l) => l.locationId === locationId);
  if (!location || location.requiresPrearrival !== true) {
    return { required: false, notes: "" };
  }
  return { required: true, notes: (location.prearrivalNotes ?? "").trim() };
}

/**
 * Returns active TerminalUpdates that apply to a voyage, based on:
 *   1. The voyage's berths — if any berth's Location.companyId matches u.companyId
 *   2. Direct locationId match on any of the voyage's berths
 *   3. berthUpdates sub-entries matching any of the voyage's berths
 *   4. Port-wide updates (no companyId) scoped to the voyage's portCallingsIds
 * This mirrors how the Terminal swim-lane view matches updates to berths.
 */
export function getPortWideUpdates(
  portCallingsIds: string[],
  terminalUpdates: TerminalUpdate[],
  berthIds: string[] = [],
  locations: Location[] = [],
  ownerCompanyIds: string[] = []
) {
  const now = new Date();
  // Set of company IDs that this voyage's berths belong to
  const voyageCompanyIds = new Set<string>(ownerCompanyIds.filter(Boolean));
  const voyageBerthIds = new Set(berthIds);
  berthIds.forEach((bid) => {
    const loc = locations.find((l) => l.locationId === bid);
    const cid = (loc as Record<string, any> | undefined)?.companyId;
    if (cid) voyageCompanyIds.add(cid as string);
  });
  const portCallingsSet = new Set(portCallingsIds);

  return terminalUpdates.filter((u) => {
    if (u.effectiveEnd && new Date(u.effectiveEnd) <= now) return false;
    // Terminal-company scoped: match if any voyage berth is at that terminal
    if (u.companyId && voyageCompanyIds.has(u.companyId)) return true;
    // Direct berth match
    if (u.locationId && voyageBerthIds.has(u.locationId)) return true;
    // Sub-entry berth match
    if (u.berthUpdates?.some((b) => voyageBerthIds.has(b.locationId))) return true;
    // Port-wide (no company, no location) scoped to the voyage's ports
    if (
      !u.companyId &&
      !u.locationId &&
      (!u.portCallingsId || portCallingsSet.has(u.portCallingsId))
    ) {
      return true;
    }
    return false;
  });
}
